import { ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import { Button } from "../../../../components/ui/common/button";
import { CardHeader } from "../../../../components/ui/common/card";
import { RecipeTypeClassification } from "../../../../config/enums/recipeSimulator.enum";

interface ProcessorCardHeaderProps {
  index: number;
  processName: string;
  dataType?: string;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onRemove: () => void;
  recipeTypeClassification?: RecipeTypeClassification;
}

const ProcessorCardHeader = ({
  index,
  processName,
  dataType,
  isCollapsed,
  onToggleCollapse,
  onRemove,
  recipeTypeClassification
}: ProcessorCardHeaderProps) => {
  const formatDataType = (type?: string) => {
    if (!type) return "";
    return type.replace(/_/g, " ");
  };

  // Helper to get badge color and text for recipe type
  const getRecipeTypeBadge = () => {
    if (!recipeTypeClassification || recipeTypeClassification === RecipeTypeClassification.PRIVATE) {
      return null; // Don't show badge for Private (default)
    }

    const badgeConfig = {
      [RecipeTypeClassification.PUBLIC]: {
        text: "Public",
        className: "tw-bg-blue-100 tw-text-blue-700 tw-border-blue-200"
      },
      [RecipeTypeClassification.DEMOKIT]: {
        text: "Demokit",
        className: "tw-bg-orange-100 tw-text-orange-700 tw-border-orange-200"
      }
    };

    const config = badgeConfig[recipeTypeClassification];
    if (!config) return null;

    return (
      <span
        className={`tw-text-[10px] tw-px-1.5 tw-py-0.5 tw-rounded-full tw-border tw-font-medium ${config.className}`}
      >
        {config.text}
      </span>
    );
  };

  return (
    <CardHeader className="tw-bg-secondary/50 tw-py-3.5 tw-px-3.5 tw-rounded-t-lg">
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-w-full">
        <button
          type="button"
          onClick={onToggleCollapse}
          className="tw-flex tw-items-center tw-gap-2 tw-flex-1 tw-min-w-0 tw-text-left hover:tw-opacity-80 tw-transition-opacity"
        >
          <span className="tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded-full tw-bg-primary tw-text-primary-foreground tw-text-xs tw-font-bold tw-shrink-0">
            {index + 1}
          </span>
          <span className="tw-font-medium tw-text-sm tw-text-foreground tw-truncate">
            {processName || `Process ${index + 1}`}
          </span>
          {dataType && (
            <span className="tw-text-xs tw-bg-muted tw-text-muted-foreground tw-px-1 tw-py-0.5 tw-rounded-full tw-capitalize tw-shrink-0">
              {formatDataType(dataType)}
            </span>
          )}
          {getRecipeTypeBadge()}
        </button>

        <div className="tw-flex tw-items-center tw-gap-1 tw-shrink-0">
          <button
            type="button"
            onClick={onToggleCollapse}
            className="tw-inline-flex tw-items-center tw-justify-center hover:tw-opacity-80 tw-transition-opacity"
            aria-label={isCollapsed ? "Expand process" : "Collapse process"}
          >
            {isCollapsed ? (
              <ChevronDown className="tw-h-4 tw-w-4 tw-text-muted-foreground" />
            ) : (
              <ChevronUp className="tw-h-4 tw-w-4 tw-text-muted-foreground" />
            )}
          </button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onRemove}
            className="tw-text-destructive hover:tw-text-destructive hover:tw-bg-destructive/10 tw-shrink-0 tw-h-8 tw-w-8"
          >
            <Trash2 className="tw-h-4 tw-w-4" />
          </Button>
        </div>
      </div>
    </CardHeader>
  );
};

export default ProcessorCardHeader;
